import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom"; // <-- Added
import { fetchTranslations } from "../actions/languageActions";
import NewHeader from "../components/Header/NewHeader";
import Footer from "../components/Footer/Footer";
import Loading from "../components/loading/Loading";
import "./StudyPathways.css"; 

const StudyPathways = () => {
  const [activePartner, setActivePartner] = useState("sp-scu");
  const navigate = useNavigate(); // <-- Added

  // (Removed the window.scrollTo(0,0) from here so it doesn't fight the form scroll)

  const dispatch = useDispatch();
  const selectedLanguage = useSelector((state) => state.language.selectedLanguage);
  const translations = useSelector((state) => state.language.translations[selectedLanguage]);

  useEffect(() => {
    dispatch(fetchTranslations(selectedLanguage));
  }, [dispatch, selectedLanguage]);

  if (!translations) {
    return <Loading />;
  }

  const showPartner = (id) => setActivePartner(id);

  // <-- Added custom click handler to fix the scroll issue
  const handleContactClick = (e) => {
    e.preventDefault(); 
    navigate("/contact-us"); 
    
    // Wait for the contact page to load, then scroll perfectly
    setTimeout(() => {
      const element = document.getElementById("cForm1");
      if (element) {
        const yOffset = -100; // Adjust this if your header covers the form
        const y = element.getBoundingClientRect().top + window.scrollY + yOffset;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    }, 800); // 800ms gives the iframe time to load
  };

  return (
    <>
      <Helmet>
        <title>University Pathways | Signet Institute</title>
        <meta
          name="description"
          content="A scalable preview of Signet Institute university pathways."
          data-rh="true"
        />
      </Helmet>

      <NewHeader />

      <div className="sp-page">
        <main>
          {/* REDESIGNED HERO SECTION WITH IMAGE */}
          <section className="sp-hero">
            <div className="sp-wrap sp-hero-grid">
              <div className="sp-hero-content">
                <div className="sp-eyebrow">Signet Institute · University Pathways</div>
                <h1>
                  Take Your Signet qualification <span>Further.</span>
                </h1>
                <p>
                  Explore study pathways from selected Signet qualifications to
                  university degrees. Each partner and pathway uses the same clear
                  format, so students can compare course details, credit
                  information and next steps.
                </p>
                <a className="sp-btn sp-btn-gold" href="#sp-pathways">
                  Explore pathways ↓
                </a>
              </div>
              
              {/* Right side image to fill empty space */}
              <div className="sp-hero-image-wrap">
                <img 
                  src="/images/study-pathways-img.png" 
                  alt="Students graduating and moving to university" 
                  className="sp-hero-img" 
                />
              </div>
            </div>
          </section>

          <section className="sp-section" id="sp-pathways">
            <div className="sp-wrap">
              <div className="sp-intro">
                <div>
                  <div className="sp-eyebrow">Find your pathway</div>
                  <h2>One consistent format. More university options.</h2>
                  <p>
                    Pathways are grouped by university partner. Every route
                    shows the Signet course, duration and campus, followed by
                    the destination degree and credit information.
                  </p>
                </div>
                {/* <-- Updated onClick event here */}
                <a className="sp-btn sp-btn-outline" href="/contact-us/#cForm1" onClick={handleContactClick}>
                  Ask about a pathway →
                </a>
              </div>

              <div className="sp-partner-tabs">
                <button
                  className={`sp-tab ${activePartner === "sp-scu" ? "sp-active" : ""}`}
                  onClick={() => showPartner("sp-scu")}
                >
                  Southern Cross University
                </button>
              </div>

              <div id="sp-scu" hidden={activePartner !== "sp-scu"}>
                <div className="sp-partner-heading">
                  <div>
                    <strong>Southern Cross University</strong>
                    <br />
                    <span>Current pathway group · 5 pathway options</span>
                  </div>
                </div>

                <div className="sp-cards">
                  {/* CARD 1 */}
                  <article className="sp-card">
                    <div className="sp-card-meta">
                      <span>Business · Leadership</span>
                      <span className="sp-credit-tag">8 units credit</span>
                    </div>
                    <div className="sp-row">
                      <div className="sp-source">
                        <span className="sp-label">Your Signet qualification</span>
                        <h3>Diploma of Leadership and Management</h3>
                        <span className="sp-course-code">BSB50420 / BSB51918</span>
                        <div className="sp-facts">
                          <span><b>Duration</b>52 weeks</span>
                          <span><b>Campuses</b>Melbourne · Sydney · Adelaide</span>
                        </div>
                        <a className="sp-link" href="https://www.signet.edu.au/business-courses/diploma-of-leadership-and-management/">
                          Signet course details ↗
                        </a>
                      </div>
                      <div className="sp-destination">
                        <img className="sp-uni-logo" src="https://www.scu.edu.au/media/dep-site-assets/images/logo-print.420af8f0.png" alt="Southern Cross University" />
                        <div className="sp-dest-content">
                          <span className="sp-label">Continue at Southern Cross University</span>
                          <div className="sp-dest-head">
                            <div>
                              <h3>Bachelor of Business</h3>
                              <span className="sp-course-code">Course code 3007255</span>
                            </div>
                            <div className="sp-percent">33%<small>8 of 24 units</small></div>
                          </div>
                          <a className="sp-dest-link" href="https://www.scu.edu.au/study/courses/bachelor-of-business-3007255/">
                            Course information ↗
                          </a>
                        </div>
                      </div>
                    </div>
                  </article>

                  {/* CARD 2 */}
                  <article className="sp-card">
                    <div className="sp-card-meta">
                      <span>Business · Leadership</span>
                      <span className="sp-credit-tag">12 units credit</span>
                    </div>
                    <div className="sp-row">
                      <div className="sp-source">
                        <span className="sp-label">Your Signet qualification</span>
                        <h3>Advanced Diploma of Leadership and Management</h3>
                        <span className="sp-course-code">BSB60420</span>
                        <div className="sp-facts">
                          <span><b>Duration</b>52 weeks</span>
                          <span><b>Campus listed</b>Melbourne</span>
                        </div>
                        <a className="sp-link" href="https://www.signet.edu.au/business-courses/advanced-diploma-of-leadership-and-management/">
                          Signet course details ↗
                        </a>
                      </div>
                      <div className="sp-destination">
                        <img className="sp-uni-logo" src="https://www.scu.edu.au/media/dep-site-assets/images/logo-print.420af8f0.png" alt="Southern Cross University" />
                        <div className="sp-dest-content">
                          <span className="sp-label">Continue at Southern Cross University</span>
                          <div className="sp-dest-head">
                            <div>
                              <h3>Bachelor of Business</h3>
                              <span className="sp-course-code">Course code 3007255</span>
                            </div>
                            <div className="sp-percent">50%<small>12 of 24 units</small></div>
                          </div>
                          <a className="sp-dest-link" href="https://www.scu.edu.au/study/courses/bachelor-of-business-3007255/">
                            Course information ↗
                          </a>
                        </div>
                      </div>
                    </div>
                  </article>

                  {/* CARD 3 */}
                  <article className="sp-card">
                    <div className="sp-card-meta">
                      <span>Community services</span>
                      <span className="sp-credit-tag">8 units credit</span>
                    </div>
                    <div className="sp-row">
                      <div className="sp-source">
                        <span className="sp-label">Your Signet qualification</span>
                        <h3>Diploma of Community Services</h3>
                        <span className="sp-course-code">CHC52021</span>
                        <div className="sp-facts">
                          <span><b>Duration</b>104 weeks</span>
                          <span><b>Campuses</b>Melbourne · Sydney · Adelaide</span>
                        </div>
                        <a className="sp-link" href="https://www.signet.edu.au/community-courses/diploma-of-community-services/">
                          Signet course details ↗
                        </a>
                      </div>
                      <div className="sp-destination">
                        <img className="sp-uni-logo" src="https://www.scu.edu.au/media/dep-site-assets/images/logo-print.420af8f0.png" alt="Southern Cross University" />
                        <div className="sp-dest-content">
                          <span className="sp-label">Continue at Southern Cross University</span>
                          <div className="sp-dest-head">
                            <div>
                              <h3>Bachelor of Community Welfare</h3>
                              <span className="sp-course-code">Course code 3007268</span>
                            </div>
                            <div className="sp-percent">33%<small>8 of 24 units</small></div>
                          </div>
                          <a className="sp-dest-link" href="https://www.scu.edu.au/study/courses/bachelor-of-community-welfare-3007268/">
                            Course information ↗
                          </a>
                        </div>
                      </div>
                    </div>
                  </article>

                  {/* CARD 4 */}
                  <article className="sp-card">
                    <div className="sp-card-meta">
                      <span>Community services</span>
                      <span className="sp-credit-tag">8 units credit</span>
                    </div>
                    <div className="sp-row">
                      <div className="sp-source">
                        <span className="sp-label">Your Signet qualification</span>
                        <h3>Diploma of Community Services</h3>
                        <span className="sp-course-code">CHC52021</span>
                        <div className="sp-facts">
                          <span><b>Duration</b>104 weeks</span>
                          <span><b>Campuses</b>Melbourne · Sydney · Adelaide</span>
                        </div>
                        <a className="sp-link" href="https://www.signet.edu.au/community-courses/diploma-of-community-services/">
                          Signet course details ↗
                        </a>
                      </div>
                      <div className="sp-destination">
                        <img className="sp-uni-logo" src="https://www.scu.edu.au/media/dep-site-assets/images/logo-print.420af8f0.png" alt="Southern Cross University" />
                        <div className="sp-dest-content">
                          <span className="sp-label">Continue at Southern Cross University</span>
                          <div className="sp-dest-head">
                            <div>
                              <h3>Bachelor of Social Work</h3>
                              <span className="sp-course-code">Course code 3007316</span>
                            </div>
                            <div className="sp-percent">25%<small>8 of 32 units</small></div>
                          </div>
                          <a className="sp-dest-link" href="https://www.scu.edu.au/study/courses/bachelor-of-social-work-3007316/">
                            Course information ↗
                          </a>
                        </div>
                      </div>
                    </div>
                  </article>

                  {/* CARD 5 */}
                  <article className="sp-card">
                    <div className="sp-card-meta">
                      <span>Construction · Engineering</span>
                      <span className="sp-credit-tag">3 units approved</span>
                    </div>
                    <div className="sp-row">
                      <div className="sp-source">
                        <span className="sp-label">Your Signet qualification</span>
                        <h3>Advanced Diploma of Civil Construction Design</h3>
                        <span className="sp-course-code">RII60520</span>
                        <div className="sp-facts">
                          <span><b>Duration</b>104 weeks</span>
                          <span><b>Campuses</b>Melbourne · Sydney · Adelaide</span>
                        </div>
                        <a className="sp-link" href="https://www.signet.edu.au/construction-courses/advanced-diploma-of-civil-construction-design/">
                          Signet course details ↗
                        </a>
                      </div>
                      <div className="sp-destination">
                        <img className="sp-uni-logo" src="https://www.scu.edu.au/media/dep-site-assets/images/logo-print.420af8f0.png" alt="Southern Cross University" />
                        <div className="sp-dest-content">
                          <span className="sp-label">Continue at Southern Cross University</span>
                          <div className="sp-dest-head">
                            <div>
                              <h3>Bachelor of Engineering (Honours)</h3>
                              <span className="sp-course-code">Course code 3508007</span>
                            </div>
                            <div className="sp-percent">9%<small>3 of 32 units</small></div>
                          </div>
                          <a className="sp-dest-link" href="https://www.scu.edu.au/study/courses/bachelor-of-engineering-honours-3508007/">
                            Course information ↗
                          </a>
                        </div>
                      </div>
                    </div>
                    <div className="sp-notice" style={{ margin: 0, borderLeft: 0, borderTop: "1px solid #efdfbc" }}>
                      <b>Additional credit may be available.</b> SCU’s Course Coordinator may assess further credit case by case.
                    </div>
                  </article>
                </div>

                <div className="sp-notice">
                  <b>Credit and admission are subject to the university’s assessment.</b> The figures shown reflect the pathway information provided for this review. SCU publicly lists the 8-unit Diploma of Leadership and Management to Bachelor of Business pathway; confirm other outcomes and all promotional wording with each university before publication.
                </div>
              </div>

            </div>
          </section>

          {/* GOLD CTA SECTION */}
          <section className="sp-cta-gold">
            <div className="sp-wrap sp-cta-inner">
              <div className="sp-cta-content">
                <h2>Ready to take the next step?</h2>
                <p>Speak with our admissions team to find the right university pathway for your Signet qualification.</p>
              </div>
              <div className="sp-cta-actions">
                {/* <-- Updated onClick event here too */}
                <a className="sp-btn sp-btn-dark" href="/contact-us/#cForm1" onClick={handleContactClick}>
                  Ask about a pathway →
                </a>
              </div>
            </div>
          </section>

        </main>
      </div>

      <Footer />
    </>
  );
};

export default StudyPathways;